import { fetchQuery, graphql, readInlineData } from 'react-relay';

import { startCaptureGraphQlErrors, stopCaptureGraphQlErrors } from '@atlassian/relay-errors';

import PermissionQuery, {
	type loomUserPermissionQuery,
} from './__generated__/loomUserPermissionQuery.graphql.ts';
import type {
	loomPersonalization_entryPoints$key,
	loomPersonalization_entryPoints$data,
} from './__generated__/loomPersonalization_entryPoints.graphql.ts';
import type {
	loomPersonalization_tenant$key,
	loomPersonalization_tenant$data,
} from './__generated__/loomPersonalization_tenant.graphql.ts';
import type {
	loomPersonalization_permission$key,
	loomPersonalization_permission$data,
} from './__generated__/loomPersonalization_permission.graphql.ts';
import { createAtlAttributionHeader } from './utils/createAtlAttributionHeader.ts';

const entryPointsFragment = graphql`
	fragment loomPersonalization_entryPoints on Query @inline {
		confluence_loomEntryPoints(cloudId: $cloudId) {
			isLoomEntryPointsEnabled
		}
		loom_settings(siteId: $cloudId) @include(if: $includeMeetingRecorderEligibility) {
			meetingRecordingsAvailable
			meetingNotesEligible
		}
		loom_connectedCalendars(siteId: $cloudId) @include(if: $includeMeetingRecorderEligibility) {
			guid
		}
	}
`;

const tenantFragment = graphql`
	fragment loomPersonalization_tenant on Query @inline {
		confluence_tenantContext {
			licensedProducts {
				licenseStatus
				productKey
			}
		}
	}
`;

const permissionFragment = graphql`
	fragment loomPersonalization_permission on Query @inline {
		bulkPermitted(
			principalIds: [$principalId]
			resourceIds: [$loomResourceId]
			permissionIds: ["write"]
		) {
			principalId
			resourceId
			permissionId
			permitted
		}
	}
`;

export type LoomPersonalizationInputs = {
	entryPoints: { data: loomPersonalization_entryPoints$data | null; hasErrors: boolean };
	tenant: { data: loomPersonalization_tenant$data | null };
};

export type LoomPersonalizationResult =
	| LoomPersonalizationInputs
	| { retryLegacy: true }
	| undefined;

export function readLoomPersonalizationInputs(
	data: loomPersonalization_entryPoints$key & loomPersonalization_tenant$key,
	entryPointsHaveErrors: boolean,
): LoomPersonalizationInputs {
	return {
		entryPoints: {
			data: readInlineData(entryPointsFragment, data),
			hasErrors: entryPointsHaveErrors,
		},
		tenant: { data: readInlineData(tenantFragment, data) },
	};
}

export async function preloadLoomPersonalizationPermission(
	variables: loomUserPermissionQuery['variables'],
	cloudId: string,
	activationId: string,
): Promise<{ data: loomPersonalization_permission$data | null; hasErrors: boolean }> {
	const collector = startCaptureGraphQlErrors();
	try {
		const result = await fetchQuery<loomUserPermissionQuery>(
			window.GLOBAL_RELAY_ENV!,
			PermissionQuery,
			variables,
			{
				fetchPolicy: 'store-or-network',
				networkCacheConfig: {
					metadata: { headers: createAtlAttributionHeader(cloudId, activationId) },
				},
			},
		).toPromise();
		return {
			data:
				readInlineData<loomPersonalization_permission$key>(permissionFragment, result ?? null) ??
				null,
			hasErrors: stopCaptureGraphQlErrors(collector).some(
				({ meta, errors }) =>
					meta.operationName === PermissionQuery.params.name && errors.length > 0,
			),
		};
	} finally {
		stopCaptureGraphQlErrors(collector);
	}
}
